/**
 * INNTIL — videoflisene i produktrutenettet.
 *
 * Laster og spiller en flis først når den er i nærheten av skjermen, og
 * pauser den når den ikke er. Filene er store — en av produktvideoene
 * deres er 10,8 MB for 11 sekunder — så `preload="none"` i markupen og
 * denne vakten er det som gjør flisene forsvarlige på mobil.
 *
 * HVORFOR EN OBSERVER OG IKKE `autoplay`. Chrome pauser riktignok
 * autospillende video utenfor skjermen av seg selv, men Safari og Firefox
 * gjør det ikke likt, og `autoplay` starter nedlastingen uansett. Med
 * observer laster vi ikke en eneste byte før flisen nærmer seg.
 *
 * HVORFOR EN MutationObserver I TILLEGG. Rutenettet re-rendres ved
 * filtrering og uendelig rulling, og Horizon morfer det inn. Nye fliser
 * kommer da inn i DOM-en uten at noe fyrer. Samme form som kryssalget i
 * handlekurva, der blokka forsvant etter første klikk fordi elementet ble
 * gjenbrukt med tomme barn — målt 23.09.2026.
 *
 * Bygget 23.09.2026.
 */
(() => {
  const MARGIN = '200px';
  const sett = new WeakSet();
  /* Flisene som er i synsfeltet NAA. Trengs fordi nettleseren kan pause en
     flis uten at observeren fyrer, og da har vi ingen annen liste aa
     gjenoppta fra. Vanlig Set, ikke WeakSet: vi maa kunne iterere. */
  const iSyne = new Set();
  /* Flisene vi selv har pauset fordi de rullet ut av synsfeltet. Trengs for aa
     skille vaar egen pause fra en nettleseren gjoer paa eget initiativ. */
  const vaarPause = new WeakSet();

  const spill = (v) => {
    if (!v.paused) return;
    if (document.visibilityState !== 'visible') return;
    const lovnad = v.play();
    if (lovnad) {
      lovnad.catch((feil) => {
        v.dataset.inntilFeil = feil.name + ': ' + feil.message.slice(0, 60);
      });
    }
  };

  const vakt = new IntersectionObserver(
    (treff) => {
      for (const t of treff) {
        const v = t.target;
        if (t.isIntersecting) {
          /* INGEN load() FØR play(). Kalles de rett etter hverandre,
             avbryter lastingen avspillingen, og løftet avvises med
             «The play() request was interrupted by a new load request».
             Med `src` satt og `preload="none"` henter nettleseren det den
             trenger av seg selv når play() kalles. Målt 23.09.2026:
             videoen sto på readyState 4 og spilte likevel ikke. */
          iSyne.add(v);
          spill(v);
        } else {
          iSyne.delete(v);
          if (!v.paused) {
            vaarPause.add(v);
            v.pause();
          }
        }
      }
    },
    { rootMargin: MARGIN, threshold: 0.1 }
  );

  const koble = (rot) => {
    const noder = (rot instanceof Element ? rot : document).querySelectorAll('[data-inntil-videoflis]');
    for (const v of noder) {
      if (sett.has(v)) continue;
      sett.add(v);
      /* muted må settes som egenskap, ikke bare attributt: Safari nekter
         å spille uten brukerhandling hvis egenskapen ikke er satt. */
      v.muted = true;

      /* LOOPEN STOPPER NOEN GANGER AV SEG SELV.
         To kjente aarsaker, og begge ser like ut for den som ser paa:

         1. Chrome pauser video uten lydspor for aa spare stroem. Skjer ogsaa
            mens fanen staar aapen, saa `visibilitychange` fanger det ikke.
         2. Med `preload="none"` kan nettleseren kaste bufferet naar klippet er
            spilt gjennom. Da fyrer `ended` i stedet for at `loop` tar over, og
            flisen blir staaende paa siste bilde.

         Meldt av Benedikte 25.09.2026. Vi gjenopptar i begge tilfeller, men
         BARE naar flisen fortsatt er i synsfeltet og sida er synlig — ellers
         ville vi kjempet mot vaar egen pause og holdt videoen i gang utenfor
         skjermen, som er nettopp det lastevakten skal hindre. */
      v.addEventListener('pause', () => {
        if (vaarPause.has(v)) {
          vaarPause.delete(v);
          return;
        }
        if (iSyne.has(v)) spill(v);
      });

      v.addEventListener('ended', () => {
        if (!iSyne.has(v)) return;
        try {
          v.currentTime = 0;
        } catch (e) {
          /* Kan kaste hvis mediet ikke er soekbart ennaa. Da forsoeker vi
             play() likevel — verste utfall er at den blir staaende, som er
             tilstanden vi allerede har. */
        }
        spill(v);
      });

      vakt.observe(v);
    }
  };

  const start = () => {
    koble(document);
    new MutationObserver((endringer) => {
      for (const e of endringer) {
        for (const n of e.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-inntil-videoflis]')) koble(n.parentElement || document);
          else if (n.querySelector && n.querySelector('[data-inntil-videoflis]')) koble(n);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  /* GJENOPPTA NAAR FANEN BLIR SYNLIG IGJEN.
     Chrome pauser video uten lydspor naar siden er skjult, for aa spare
     stroem: «The play() request was interrupted because video-only
     background media was paused to save power». Observeren fyrer ikke naar
     fanen kommer tilbake, for flisen har ikke flyttet seg i synsfeltet, og
     da blir den staaende frosset. Maalt 25.09.2026 paa
     /collections/alle-produkter: flisen var synlig, readyState 4, og
     likevel `paused`.

     Samme grep gjelder bytte mellom vinduer og mobil som laaser skjermen. */
  const gjenoppta = () => {
    if (document.visibilityState !== 'visible') return;
    for (const v of iSyne) spill(v);
  };
  document.addEventListener('visibilitychange', gjenoppta);
  window.addEventListener('pageshow', gjenoppta);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
